import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { MultiRangeSelectComponent } from "@components/multi-range-select/multi-range-select.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject, Subscription } from "rxjs";
import { delay, takeUntil } from "rxjs/operators";
import { ToastService } from "~app/common/components/toast/toast.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { MaskinlagService } from "~app/common/services/maskinlag.service";
import { IMaskinlag } from "~app/features/planera/model/IMaskinLag";
import { MinaMaskinlagTexts } from "./mina-maskinlag.texts";

@Component({
    selector: "rask-mina-maskinlag",
    templateUrl: "./mina-maskinlag.component.html",
    styleUrls: ["./mina-maskinlag.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        FormsModule,
        ReactiveFormsModule,
        MultiRangeSelectComponent,
        ButtonComponent,
        SaveButtonComponent
    ]
})
export class MinaMaskinlagComponent implements OnInit, OnDestroy {
    allaMaskinlag: IMaskinlag[];
    form: UntypedFormGroup;
    isModalActive = false;
    maskinlagLoaded = false;
    maskinlagUpdated = false;
    minaMaskinlag: IMaskinlag[];
    texts = MinaMaskinlagTexts.texts;

    private readonly activeModal = inject(NgbActiveModal);
    private readonly destroy$ = new Subject<void>();
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly maskinlagService = inject(MaskinlagService);
    private readonly toastService = inject(ToastService);

    private formSubscription: Subscription;

    dirtyCheckCallback(result: IDirtyFormResult): void {

        if (result.isSkipSave) {
            if (this.isModalActive) {
                this.activeModal.dismiss();
            }
        }

        if (result.isSave) {
            if (this.isModalActive) {
                this.activeModal.dismiss();
            }
        }
    }

    dismiss() {
        if (this.dirtyFormService.canClose()) {
            this.activeModal.dismiss();
        }
    }

    readonly listDisplayName = (listItem: IMaskinlag): string => `${listItem.csrNummer} - ${listItem.namn}`;

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async ngOnInit() {
        this.allaMaskinlag = await this.maskinlagService.getAllMaskinlag();
        this.minaMaskinlag = await this.maskinlagService.getMinaMaskinlag();
        this.removeMinaMaskinLagFromAllaMaskinlag();
        this.maskinlagLoaded = true;

        this.setupForm();

        this.dirtyFormService.onDirtyNgModel(
            this.isDirty.bind(this),
            this.save.bind(this),
            d => this.dirtyCheckCallback(d),
            () => true,
            this.texts.lamnaModalHeader,
            this.texts.lamnaModalBody,
            { size: "lg",
                centered: false });
    }

    readonly orderByCsr = (arr: IMaskinlag[]): void => {
        const lessOrZero = (b: any, a: any): number => b.csrNummer > a.csrNummer ? -1 : 0;
        arr.sort((a, b) => a.csrNummer > b.csrNummer ? 1 : lessOrZero(b, a));
    };

    save = async (): Promise<void> => {
        const response = await this.maskinlagService.saveMinaMaskinlag(this.minaMaskinlag);

        this.minaMaskinlag = response.maskinlag as unknown as IMaskinlag[];
        this.maskinlagUpdated = false;

        if (!this.isModalActive) {
            this.toastService.add(this.texts.andringarSparats);
        } else {
            this.activeModal.close();
        }
    };

    private isDirty(): boolean {
        return this.maskinlagUpdated;
    }

    private readonly removeMinaMaskinLagFromAllaMaskinlag = (): void => {
        const minaMaskinlagsId = this.minaMaskinlag.map(p => p.id);
        this.allaMaskinlag = this.allaMaskinlag.filter(p => minaMaskinlagsId.indexOf(p.id) === -1);
    };

    private readonly setupForm = () => {
        this.form = this.formBuilder.group({
            minaMaskinlag: this.formBuilder.control(this.minaMaskinlag)
        });

        this.formSubscription?.unsubscribe();
        this.formSubscription = this.form.controls.minaMaskinlag
            .valueChanges
            .pipe(
                delay(0),
                takeUntil(this.destroy$)
            ).subscribe((value: IMaskinlag[]) => {
                this.minaMaskinlag = value;
                this.maskinlagUpdated = true;
            });
    };
}
